import {
  AppRegistration,
  Assessment,
  AutoFixHigh,
  Bolt,
  CalendarMonth,
  CloudSync,
  CropOriginal,
  Dashboard,
  DeviceHub,
  Dvr,
  FontDownload,
  Group,
  LiveTv,
  MovieCreation,
  OndemandVideo,
  ReceiptLong,
  Router,
  Schedule,
  Settings,
  Stadium,
  Tv,
  VerifiedUser,
  ViewComfy,
} from '@mui/icons-material';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { useAppContext, type AppContext__CurrentUser, type AppContext__Network } from '~/contexts';

export type NavItemPermission = keyof AppContext__Network & `can${string}`;

export type NavGroupKey =
  | 'top'
  | 'device'
  | 'content'
  | 'scheduling'
  | 'reporting'
  | 'user'
  | 'network';

export interface NavItemConfig {
  enabledFor?: (network: AppContext__Network) => boolean;
  group: NavGroupKey;
  icon: ReactNode;
  permission?: NavItemPermission;
  selected?: string;
  text: string;
  to: string;
}

export const useNavItems = (): NavItemConfig[] => {
  const { t } = useTranslation();

  const [deviceFilters] = useLocalStorage('devices:filters', '');
  const [signalFilters] = useLocalStorage('device-signals:filters', '');
  const [playlistFilters] = useLocalStorage('playlists:filters', '');
  const [showFilters] = useLocalStorage('shows:filters', '');
  const [studioFilters] = useLocalStorage('studio:filters', '');

  return useMemo(
    () => [
      {
        group: 'top',
        icon: <Dashboard />,
        text: t('common.navigation.dashboard'),
        to: '/dashboard',
      },
      {
        enabledFor: (network) => network.settings.enableLiveChannelStreaming,
        group: 'top',
        icon: <LiveTv />,
        permission: 'canAccessLiveChannelStreams',
        text: t('common.navigation.live_channels'),
        to: '/live-channels',
      },
      {
        group: 'device',
        icon: <Router />,
        permission: 'canAccessDevices',
        selected: 'devices',
        text: t('common.navigation.devices'),
        to: deviceFilters ? `/devices?${deviceFilters}` : '/devices',
      },
      {
        group: 'device',
        icon: <DeviceHub />,
        permission: 'canManage',
        text: t('common.navigation.device_groups'),
        to: '/device-groups',
      },
      {
        group: 'device',
        icon: <Bolt />,
        permission: 'canManage',
        selected: 'device-signals',
        text: t('common.navigation.signals'),
        to: signalFilters ? `/device-signals?${signalFilters}` : '/device-signals',
      },
      {
        group: 'device',
        icon: <Tv />,
        permission: 'canManage',
        text: t('common.navigation.tv_brands'),
        to: '/tv-brands',
      },
      {
        group: 'content',
        icon: <CropOriginal />,
        permission: 'canAccessContent',
        text: t('common.navigation.content'),
        to: '/content',
      },
      {
        group: 'content',
        icon: <OndemandVideo />,
        permission: 'canAccessPlaylists',
        selected: 'playlists',
        text: t('common.navigation.playlists'),
        to: playlistFilters ? `/playlists?${playlistFilters}` : '/playlists',
      },
      {
        group: 'content',
        icon: <MovieCreation />,
        permission: 'canAccessShows',
        selected: 'shows',
        text: t('common.navigation.shows'),
        to: showFilters ? `/shows?${showFilters}` : '/shows',
      },
      {
        group: 'content',
        icon: <AutoFixHigh />,
        permission: 'canAccessStudio',
        selected: 'studio',
        text: t('common.navigation.studio'),
        to: studioFilters ? `/studio?${studioFilters}` : '/studio',
      },
      {
        group: 'content',
        icon: <ViewComfy />,
        permission: 'canManage',
        text: t('common.navigation.layouts'),
        to: '/layouts',
      },
      {
        group: 'content',
        icon: <AppRegistration />,
        permission: 'canAccessTemplates',
        text: t('common.navigation.templates'),
        to: '/templates',
      },
      {
        group: 'content',
        icon: <FontDownload />,
        permission: 'canAccessFonts',
        text: t('common.navigation.fonts'),
        to: '/fonts',
      },
      {
        group: 'scheduling',
        icon: <CalendarMonth />,
        permission: 'canAccessScheduler',
        text: t('common.navigation.events'),
        to: '/events',
      },
      {
        group: 'scheduling',
        icon: <Schedule />,
        permission: 'canAccessScheduler',
        text: t('common.navigation.manual'),
        to: '/schedules',
      },
      {
        group: 'scheduling',
        icon: <Stadium />,
        permission: 'canManage',
        text: t('common.navigation.event_types'),
        to: '/event-types',
      },
      {
        group: 'reporting',
        icon: <Assessment />,
        permission: 'canAccessReporting',
        selected: 'playout-reports',
        text: t('common.navigation.playout_reporting'),
        to: '/playout-reports',
      },
      {
        group: 'reporting',
        icon: <ReceiptLong />,
        permission: 'canManage',
        text: t('common.navigation.audit_logs'),
        to: '/audit-logs',
      },
      {
        group: 'user',
        icon: <Group />,
        permission: 'canManage',
        text: t('common.navigation.users'),
        to: '/users',
      },
      {
        group: 'user',
        icon: <VerifiedUser />,
        permission: 'canManage',
        text: t('common.navigation.roles'),
        to: '/roles',
      },
      {
        group: 'network',
        icon: <Settings />,
        permission: 'canManage',
        text: t('common.navigation.settings'),
        to: '/settings',
      },
      {
        group: 'network',
        icon: <OndemandVideo />,
        permission: 'canManage',
        text: t('common.navigation.channels'),
        to: '/channels',
      },
      {
        group: 'network',
        icon: <Dvr />,
        permission: 'canManage',
        text: t('common.navigation.channel_guides'),
        to: '/channel-guides',
      },
      {
        group: 'network',
        icon: <CloudSync />,
        permission: 'canAccessDataSources',
        text: t('common.navigation.data_sources'),
        to: '/data-sources',
      },
    ],
    [deviceFilters, playlistFilters, showFilters, signalFilters, studioFilters, t],
  );
};

export const isNavItemVisible = (
  item: NavItemConfig,
  network: AppContext__Network,
  user: AppContext__CurrentUser,
) =>
  !(user.hideDashboard && item.to === '/dashboard') &&
  (item.enabledFor?.(network) ?? true) &&
  (item.permission === undefined || network[item.permission].value);

export interface NavGroupConfig {
  initialOpen: boolean;
  items: NavItemConfig[];
  key: NavGroupKey;
  title: string;
}

export const useNavGroupItems = (group: NavGroupKey) => {
  const { currentNetwork, currentUser } = useAppContext();
  const items = useNavItems();

  return useMemo(
    () =>
      items.filter(
        (item) => item.group === group && isNavItemVisible(item, currentNetwork, currentUser),
      ),
    [currentNetwork, currentUser, group, items],
  );
};

export const useNavGroups = (): NavGroupConfig[] => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const items = useNavItems();

  return useMemo(() => {
    const groups: Omit<NavGroupConfig, 'items'>[] = [
      { initialOpen: true, key: 'device', title: t('common.navigation.device_management') },
      { initialOpen: true, key: 'content', title: t('common.navigation.content_management') },
      { initialOpen: false, key: 'scheduling', title: t('common.navigation.scheduling') },
      { initialOpen: false, key: 'reporting', title: t('common.navigation.reporting') },
      { initialOpen: false, key: 'user', title: t('common.navigation.user_management') },
      { initialOpen: false, key: 'network', title: t('common.navigation.network_management') },
    ];

    return groups.map((group) => ({
      ...group,
      items: items.filter(
        (item) => item.group === group.key && isNavItemVisible(item, currentNetwork, currentUser),
      ),
    }));
  }, [currentNetwork, currentUser, items, t]);
};
